import { useLocation } from 'react-router';

import { consultationCta, type NavLink } from '@/config/navigation';

import { useFieldFocused } from './useFieldFocused';
import { usePageWhatsAppMessage } from './usePageWhatsAppMessage';
import { useWhatsAppContact } from './useWhatsAppContact';

/**
 * Pages with one action of their own. A fixed bar offering two more competes
 * with the single thing the page is asking for, so it stands down: checkout
 * has its one submit, and the cart has its one route onward.
 */
const HIDDEN_ON = ['/cars/checkout', '/cars/cart'];

export type StickyActionBarModel = {
  /** False slides the bar off the bottom; the bar stays mounted either way. */
  visible: boolean;
  /** Prefilled message for the route, or null when no number is configured. */
  whatsappMessage: string | null;
  /** The advice CTA, or null on the page it points at. */
  consultation: NavLink | null;
};

/**
 * Everything the phone action bar needs to decide, in one place.
 *
 * The bar itself is a surface and two buttons; all four reasons it changes
 * shape live here, so the organism stays a layout and this stays testable
 * without rendering MUI.
 *
 * The consultation CTA is read from `config/navigation` rather than retyped:
 * it is the same `Get Free Advice` the header shows from `lg` up and the
 * drawer shows below it, and the whole point of the bar is that a phone — the
 * one width where the header has no room for that pill — gets it back. Which
 * is also why the bar no longer carries `Get a Quote`: that pill renders in
 * the header at every width including `xs`, so repeating it here spent half
 * the bar on a route the visitor could already see.
 *
 * On `/free-import-advice` the CTA would point at the current page, so it
 * drops out and WhatsApp takes the full width rather than leaving a button
 * that navigates nowhere.
 */
export function useStickyActionBar(): StickyActionBarModel {
  const { pathname } = useLocation();
  const message = usePageWhatsAppMessage();
  const fieldFocused = useFieldFocused();
  /* Asked for presence, not for a number: the button resolves its own seat.
     This is only deciding whether the bar has a WhatsApp half to draw, which
     is false when WhatsApp is switched off site-wide. */
  const contact = useWhatsAppContact();

  const whatsappMessage = contact ? message : null;
  const consultation = pathname.startsWith(consultationCta.to) ? null : consultationCta;
  const hasAction = whatsappMessage !== null || consultation !== null;

  return {
    // An empty bar is still a 72px strip of chrome over the page, so a bar
    // with nothing left to offer hides rather than renders blank.
    visible: hasAction && !fieldFocused && !HIDDEN_ON.some((p) => pathname.startsWith(p)),
    whatsappMessage,
    consultation,
  };
}
